import {
  CitySelectField,
  InputDateField,
  InputField,
  InputNumberField,
  SelectField,
  StateSelectField,
  TextAreaField
} from './Fields';
import { Form } from 'antd';
import { IAttributeField } from 'interfaces/documentation/document-attribute-field';
import {
  IDENTITY_DOCUMENT_TYPE_FIELD,
  maxLengthDependencies,
  maxLengthRule
} from './max-length-rule';
import { getFullNameValidationError } from 'utils/validators';
import { useMemo } from 'react';

interface DocumentItemFieldProps {
  field: IAttributeField;
}

const FIELDS_MAP = {
  text: InputField,
  number: InputNumberField,
  select: SelectField,
  text_area: TextAreaField,
  state_select: StateSelectField,
  city_select: CitySelectField,
  date: InputDateField
};

interface DocumentItemFieldProps {
  field: IAttributeField;
  disabled?: boolean;
}

export const DocumentItemField = ({
  field,
  disabled
}: DocumentItemFieldProps) => {
  const { field_type, name, value, required, min, max } = field;
  const fieldId = (field as IAttributeField & { id?: string }).id;

  const Component = useMemo(() => FIELDS_MAP[field_type], [field_type]);
  const normalizedTargetFieldName = 'nome completo';
  const isFullNameField = [fieldId, name].some(
    fieldIdentifier =>
      fieldIdentifier?.trim().toLowerCase() === normalizedTargetFieldName
  );

  const generateRules = () => {
    const rules = [];

    if (min) {
      rules.push({ min, message: `Mínimo ${min} caracteres` });
    }

    const maxRule = maxLengthRule({ name, max }, IDENTITY_DOCUMENT_TYPE_FIELD);

    if (maxRule) {
      rules.push(maxRule);
    }

    if (required) {
      rules.push({ required, message: 'Campo obrigatório' });
    }

    if (isFullNameField) {
      rules.push({
        validator(_: unknown, inputValue: string) {
          const errorMessage = getFullNameValidationError(
            inputValue,
            'Nome Completo'
          );

          if (!errorMessage) {
            return Promise.resolve();
          }

          return Promise.reject(new Error(errorMessage));
        }
      });
    }

    return rules;
  };

  const rules = useMemo(generateRules, [
    isFullNameField,
    max,
    min,
    name,
    required
  ]);

  return (
    <Form.Item
      label={name}
      name={name}
      initialValue={value}
      rules={rules}
      dependencies={maxLengthDependencies(field, IDENTITY_DOCUMENT_TYPE_FIELD)}
    >
      <Component {...field} disabled={disabled} />
    </Form.Item>
  );
};
